import React from 'react';
import styled from 'styled-components';

import type { Text } from '@zillow/constellation';
import { FormField, FormHelp, Input, Label, Paragraph, Spacer, Textarea } from '@zillow/constellation';

import type { Section } from '../../../models/index';
import type { HTMLElementEvent } from '../../../models/Types';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

export const MultiFamilyTextarea = styled(Textarea)`
  &,
  &:focus {
    height: 8.625rem;
  }
`;

export const StyledTextarea = styled(Textarea)`
  &,
  &:focus {
    height: 7.125rem;
  }
`;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function PropertyAddress_v2({ section, setSectionValues }: Props): JSX.Element {
  function onPropertyAddressChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS]: value,
    });
  }

  function onUnitNumberChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.UNIT_NUMBER]: value,
    });
  }

  function onComplexAddressChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS]: value,
    });
  }

  function onYesClicked(): void {
    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX]: true,
    });
  }

  function onNoClicked(): void {
    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX]: false,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  return (
    <>
      <Paragraph marginBottom="xs" fontType="h6">
        {isMultiFamilyLandlord ? 'What is the street address of the property?' : 'What is the property’s address?'}
      </Paragraph>
      <FormField marginBottom="sm" className={FULLSTORY_EXCLUDE}>
        <StyledTextarea
          aria-label="property address"
          resize="vertical"
          value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
          error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
          onChange={onPropertyAddressChange}
        />
        {section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS) ? (
          <FormHelp error>{PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS}</FormHelp>
        ) : (
          <FormHelp>Street, city, state, ZIP Code</FormHelp>
        )}
      </FormField>
      {isMultiFamilyLandlord && (
        <>
          <FormField marginBottom="sm" className={FULLSTORY_EXCLUDE}>
            <Label id="unit-number" optional>
              What is the unit number?
            </Label>
            <Input
              aria-labelledby="unit-number"
              placeholder="(example: Apt 100 or Unit A)"
              value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.UNIT_NUMBER)}
              onChange={onUnitNumberChange}
            />
          </FormField>
          <Label>Is the property or unit part of a complex with a different address?</Label>
          <Spacer marginTop="xs" />
          <YesAndNoExpansion
            yesHandler={onYesClicked}
            noHandler={onNoClicked}
            yesActive={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PART_OF_COMPLEX)}
          >
            <FormField marginTop="lg" className={FULLSTORY_EXCLUDE}>
              <Label id="complex-address">Address of the complex</Label>
              <MultiFamilyTextarea
                aria-labelledby="complex-address"
                resize="vertical"
                error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS)}
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS)}
                onChange={onComplexAddressChange}
              />
              {section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.COMPLEX_ADDRESS) ? (
                <FormHelp error>{PROPERTY_ADDRESS.ERROR_MESSAGE.COMPLEX_ADDRESS}</FormHelp>
              ) : (
                <FormHelp>Name of complex, street, city, state, ZIP Code</FormHelp>
              )}
            </FormField>
          </YesAndNoExpansion>
        </>
      )}
    </>
  );
}
